/**
 * Fischer Hockey Product Card Standardized 1-Click Fly-by Engine
 * 
 * Features:
 * - Buttery-smooth transitions: Zoom in to blade -> continuous sweeping shaft pan -> seamless pull-back zoom out.
 * - Quick Add button is isolated, fully visible, and fully operational.
 * - Entire stick blade remains 100% in view during Phase 1 zoom without left-edge clipping.
 * - Clean dark carbon button with yellow writing on both desktop and mobile.
 * - Clicking the card anywhere else navigates directly to the Product Details Page (PDP).
 */
(function initFischerCardFlyby() {
  const activeFlybys = new WeakMap();

  function stopFlyby(gallery) {
    if (!gallery) return;

    const data = activeFlybys.get(gallery);
    if (data && data.timeouts) {
      data.timeouts.forEach((t) => clearTimeout(t));
    }
    activeFlybys.delete(gallery);

    // Smoothly pull back to scale 1 without lateral origin jumping
    gallery.style.setProperty('--zoom-scale', '1');
    gallery.style.setProperty('--zoom-duration', '0.35s');
    gallery.style.setProperty('--zoom-easing', 'cubic-bezier(0.25, 1, 0.5, 1)');

    const btn = gallery.querySelector('.fischer-card-flyby-btn');
    if (btn) {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-label', 'Play stick fly-by inspection');
    }

    setTimeout(() => {
      gallery.classList.remove('fischer-is-flyby-active');
      gallery.style.removeProperty('--zoom-x');
      gallery.style.removeProperty('--zoom-y');
      gallery.style.removeProperty('--zoom-scale');
      gallery.style.removeProperty('--zoom-duration');
      gallery.style.removeProperty('--zoom-easing');
      gallery.style.removeProperty('--flyby-duration');
      gallery.style.removeProperty('--flyby-easing');
    }, 360);
  }

  function startFlyby(gallery) {
    if (!gallery) return;

    // Stop any existing active flyby on other cards
    document.querySelectorAll('.card-gallery.fischer-is-flyby-active').forEach((g) => stopFlyby(g));

    const timeouts = [];
    activeFlybys.set(gallery, { timeouts, gallery });

    const btn = gallery.querySelector('.fischer-card-flyby-btn');
    if (btn) {
      btn.classList.add('is-active');
      btn.setAttribute('aria-label', 'Stop stick fly-by inspection');
    }

    gallery.classList.add('fischer-is-flyby-active');

    // ===============================================================
    // PHASE 1: Smooth Zoom In to Blade (0ms – 750ms)
    // Entire blade is framed safely with generous margin from the left
    // ===============================================================
    gallery.style.setProperty('--zoom-x', '0%');
    gallery.style.setProperty('--zoom-y', '50%');
    gallery.style.setProperty('--zoom-scale', '2.4');
    gallery.style.setProperty('--zoom-duration', '0.75s');
    gallery.style.setProperty('--zoom-easing', 'cubic-bezier(0.22, 1, 0.36, 1)');
    gallery.style.setProperty('--flyby-duration', '0.75s');
    gallery.style.setProperty('--flyby-easing', 'cubic-bezier(0.22, 1, 0.36, 1)');

    // ===============================================================
    // PHASE 2: Seamless Continuous Glide Down the Shaft (+750ms)
    // Flows directly out of the zoom with zero jarring pause
    // ===============================================================
    timeouts.push(setTimeout(() => {
      gallery.style.setProperty('--zoom-x', '95%');
      gallery.style.setProperty('--flyby-duration', '2.5s');
      gallery.style.setProperty('--flyby-easing', 'cubic-bezier(0.42, 0.05, 0.55, 0.95)');
    }, 750));

    // ===============================================================
    // PHASE 3: Seamless Pull-Back Zoom Out (+3250ms)
    // Smoothly pulls back from the grip into full 1.0x view without origin snap
    // ===============================================================
    timeouts.push(setTimeout(() => {
      gallery.style.setProperty('--zoom-scale', '1');
      gallery.style.setProperty('--zoom-duration', '0.75s');
      gallery.style.setProperty('--zoom-easing', 'cubic-bezier(0.25, 1, 0.5, 1)');
    }, 3250));

    // ===============================================================
    // PHASE 4: Finish & Clean Reset (+4050ms)
    // ===============================================================
    timeouts.push(setTimeout(() => {
      stopFlyby(gallery);
    }, 4050));
  }

  // -----------------------------------------------------------------
  // CLICK / TAP HANDLER (Standardized for Desktop & Mobile)
  // -----------------------------------------------------------------
  document.addEventListener('click', (e) => {
    // Quick Add button must remain completely unobstructed
    if (e.target.closest('.quick-add')) return;

    const btn = e.target.closest('.fischer-card-flyby-btn');
    if (!btn) return;

    // Shield card link navigation to PDP
    e.preventDefault();
    e.stopPropagation();

    const gallery = btn.closest('.card-gallery');
    if (!gallery) return;

    if (gallery.classList.contains('fischer-is-flyby-active')) {
      stopFlyby(gallery);
    } else {
      startFlyby(gallery);
    }
  });
})();
